import { forwardRef, useEffect, useState } from "react";
import type {
  ChangeEvent,
  FocusEvent,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from "react";

export function Field({
  label,
  help,
  children,
}: {
  label?: ReactNode;
  help?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="field">
      {label ? <label className="label">{label}</label> : null}
      {children}
      {help ? <div className="help">{help}</div> : null}
    </div>
  );
}

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>(function _Input(props, ref) {
  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    e.target.select();
    if (props.onFocus) {
      props.onFocus(e);
    }
  };

  return (
    <input
      ref={ref}
      className={`input ${props.className || ""}`}
      {...props}
      onFocus={handleFocus}
    />
  );
});

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement>
>(function _Select(props, ref) {
  return <select ref={ref} className={`select ${props.className || ""}`} {...props} />;
});

export function MoneyInput({
  value,
  onChange,
  placeholder,
  className,
  suffix,
  frequency,
  onFrequencyChange,
}: {
  value: number | string;
  onChange: (n: number) => void;
  placeholder?: string;
  className?: string;
  suffix?: string;
  frequency?: 'annual' | 'monthly';
  onFrequencyChange?: (freq: 'annual' | 'monthly') => void;
}) {
  const [displayText, setDisplayText] = useState<string>("");
  const [isFocused, setIsFocused] = useState(false);

  // Sincronizar con el valor externo
  useEffect(() => {
    if (!isFocused) {
      const num = Number(value || 0);
      setDisplayText(num === 0 ? "" : formatNumber(num));
    }
  }, [value, isFocused]);

  function formatNumber(n: number): string {
    if (!isFinite(n) || n === 0) return "";
    return n.toLocaleString("es-ES");
  }

  function parseNumber(s: string): number {
    if (!s || s.trim() === "") return 0;
    // Remover separadores de miles y convertir coma decimal a punto
    const cleaned = s.replace(/\./g, "").replace(",", ".");
    const num = parseFloat(cleaned);
    return isFinite(num) ? num : 0;
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const input = e.target.value;

    // Permitir cadena vacía
    if (input === "") {
      setDisplayText("");
      onChange(0);
      return;
    }

    // Validar que solo contenga números, comas y puntos
    const validChars = /^[0-9.,\s]*$/;
    if (!validChars.test(input)) {
      return; // Ignorar caracteres no válidos
    }

    setDisplayText(input);
  }

  function handleBlur() {
    setIsFocused(false);
    const num = parseNumber(displayText);
    onChange(num);
    setDisplayText(num === 0 ? "" : formatNumber(num));
  }

  function handleFocus(e: FocusEvent<HTMLInputElement>) {
    setIsFocused(true);
    // Si está vacío o es cero, dejar vacío para facilitar edición
    if (displayText === "" || parseNumber(displayText) === 0) {
      setDisplayText("");
    }
    e.target.select();
  }

  const hasFrequencySelect = frequency !== undefined && onFrequencyChange !== undefined;

  return (
    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
      <input
        type="text"
        className={`input money ${className || ""}`}
        inputMode="decimal"
        value={displayText}
        placeholder={placeholder || "0"}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        style={{ paddingRight: hasFrequencySelect ? "7rem" : suffix ? "28px" : undefined, width: "100%" }}
      />
      {hasFrequencySelect ? (
        <div style={{
          position: "absolute",
          right: "0",
          top: "0",
          bottom: "0",
          display: "flex",
          alignItems: "center",
          paddingRight: "8px"
        }}>
          <select
            value={frequency}
            onChange={(e) => onFrequencyChange(e.target.value as 'annual' | 'monthly')}
            style={{
              fontSize: "12px",
              color: "#6B7280",
              backgroundColor: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "2px 4px",
              outline: "none",
            }}
          >
            <option value="annual">€ / año</option>
            <option value="monthly">€ / mes</option>
          </select>
        </div>
      ) : suffix ? (
        <span style={{ position: "absolute", right: "12px", color: "var(--muted)", pointerEvents: "none", fontSize: "14px" }}>
          {suffix}
        </span>
      ) : null}
    </div>
  );
}
